// Phase-1/2 server-backed capture client adapter. Orchestrates the
// in-memory FIFO queue, the 500 ms per-itemId coalesce, LWW retry,
// exponential backoff, offline pause, and the tab-close durability
// guarantees specified in `docs/buildspec/capture/20-ui.md §8`.
// Endpoint mapping lives in `./capture-client-transport.ts`; result →
// event projection and HTTP-status classification live in
// `./capture-client-events.ts`. Together they are the ONE place the
// mutation + offline contract is implemented — the Phase-3
// PowerSync-backed adapter must pass the same contract tests
// (`capture-client.test.ts` + `capture-client-behavior.test.ts`).

import { ApiError, type ApiFetchInit } from '../../hooks/api'
import { canonicalizeQuantity } from '../../hooks/powersync/quantity-canonical'

import {
  defaultAddBeforeUnloadListener,
  defaultAddOfflineListener,
  defaultAddOnlineListener,
  defaultAddVisibilityChangeListener,
  defaultGetVisibilityState,
  defaultIsOnline,
} from './capture-client-defaults'
import {
  createCaptureEventBus,
  type CaptureEventBus,
  type CaptureEventHandler,
} from './capture-events'
import {
  eventForResult,
  extractAuthoritative,
  extractRetryAfterMs,
  extractSyncVersion,
  isPermanentClientError,
} from './capture-client-events'
import {
  dispatchToServer,
  getDefaultApiFetch,
  type ApiFetchImpl,
} from './capture-client-transport'
import type {
  CaptureClient,
  CaptureMutation,
  CaptureMutationResult,
} from './capture-client.types'
import { emit } from '../telemetry/counter-telemetry'

type Resolvers = {
  resolve: (result: CaptureMutationResult) => void
  reject: (error: unknown) => void
}

interface QueueEntry {
  id: number
  mutation: CaptureMutation
  resolvers: Resolvers[]
  attempts: number
  lwwRetried: boolean
  inFlight: boolean
  nextAttemptAt: number
  retryTimer: ReturnType<typeof setTimeout> | null
  // Parked on 401 — skipped by runQueue() until the online listener
  // clears this flag, treating post-auth-redirect as a reconnect signal
  // (20-ui §8.6a).
  authBlocked: boolean
  // Set when a newer `updateItem` for the same itemId is dispatched
  // before this one settles. handleLwwConflict() drops superseded
  // entries instead of LWW-retrying with a refreshed syncVersion —
  // otherwise a stale remove could re-apply isZeroConfirmed=true
  // on top of a re-add. (Codex P2 on PR #416.)
  superseded: boolean
}

interface CoalesceEntry {
  itemId: string
  current: Extract<CaptureMutation, { kind: 'updateItem' }>
  resolvers: Resolvers[]
  debounceTimer: ReturnType<typeof setTimeout>
}

const DEFAULT_DEBOUNCE_MS = 500
const DEFAULT_INITIAL_BACKOFF_MS = 1_000
const DEFAULT_MAX_BACKOFF_MS = 60_000

// Injection seams used by the tests. Production uses browser globals;
// tests supply a minimal stub so jsdom quirks don't leak into
// assertions.
export interface CaptureClientDeps {
  apiFetch?: <T>(path: string, init: ApiFetchInit) => Promise<T>
  debounceMs?: number
  initialBackoffMs?: number
  maxBackoffMs?: number
  isOnline?: () => boolean
  addOnlineListener?: (handler: () => void) => () => void
  addOfflineListener?: (handler: () => void) => () => void
  addBeforeUnloadListener?: (
    handler: (event: BeforeUnloadEvent) => void,
  ) => () => void
  addVisibilityChangeListener?: (handler: () => void) => () => void
  addPageHideListener?: (handler: () => void) => () => void
  getVisibilityState?: () => DocumentVisibilityState | 'visible' | 'hidden'
  eventBus?: CaptureEventBus
}

export interface CaptureClientWithInternals extends CaptureClient {
  subscribe(handler: CaptureEventHandler): () => void
  // Drain any pending in-memory coalesce to durable storage and resolve once
  // every queued local write has landed. The logout flow awaits this (via the
  // capture-client registry) BEFORE the held-count guard reads the device, so
  // a +/- tap still inside the 500 ms coalesce window is persisted to ps_crud
  // and surfaces in the guard rather than being silently destroyed by the
  // sign-out clear (issue #726). Must surface a rejection if a pending write
  // fails to persist (fail-closed) rather than resolving on a lost count.
  flush(): Promise<void>
  destroy(): void | Promise<void>
}

export function createCaptureClient(
  deps: CaptureClientDeps = {},
): CaptureClientWithInternals {
  const fetchImpl: ApiFetchImpl = deps.apiFetch ?? getDefaultApiFetch()
  const debounceMs = deps.debounceMs ?? DEFAULT_DEBOUNCE_MS
  const initialBackoffMs = deps.initialBackoffMs ?? DEFAULT_INITIAL_BACKOFF_MS
  const maxBackoffMs = deps.maxBackoffMs ?? DEFAULT_MAX_BACKOFF_MS
  const bus = deps.eventBus ?? createCaptureEventBus()

  const isOnline = deps.isOnline ?? defaultIsOnline
  const addOnlineListener = deps.addOnlineListener ?? defaultAddOnlineListener
  const addOfflineListener =
    deps.addOfflineListener ?? defaultAddOfflineListener
  const addBeforeUnloadListener =
    deps.addBeforeUnloadListener ?? defaultAddBeforeUnloadListener
  const addVisibilityChangeListener =
    deps.addVisibilityChangeListener ?? defaultAddVisibilityChangeListener
  const getVisibilityState =
    deps.getVisibilityState ?? defaultGetVisibilityState

  const queue: QueueEntry[] = []
  const coalesce = new Map<string, CoalesceEntry>()
  let nextEntryId = 0
  const cleanups: Array<() => void> = []
  // True after the first 401; cleared on `online` so post-auth-redirect
  // counts as a reconnect signal and drains the parked queue (20-ui §8.6a).
  let authExpiredEmitted = false

  cleanups.push(
    addOnlineListener(() => {
      // `online` is the universal "try again" tick — drains whatever
      // parked the queue (offline, backoff, 401 auth gap).
      for (const entry of queue) entry.authBlocked = false
      authExpiredEmitted = false
      runQueue()
    }),
  )
  cleanups.push(
    addOfflineListener(() => {
      // No-op: we pause passively via `isOnline()` checks inside the
      // worker loop. The listener is wired so test stubs can observe
      // that the adapter installed it, and future telemetry has a
      // ready seam.
    }),
  )
  cleanups.push(
    addBeforeUnloadListener((event) => {
      if (pendingCount() === 0) return
      // Any truthy return triggers the browser's native "Leave site?"
      // prompt. We set both preventDefault (modern spec) and
      // returnValue (classic path) for maximum compatibility.
      event.preventDefault()
      event.returnValue = ''
    }),
  )
  cleanups.push(
    addVisibilityChangeListener(() => {
      if (getVisibilityState() !== 'hidden') return
      // Best-effort flush: clear remaining backoff on every entry and
      // collapse every open coalesce window so the OS doesn't freeze
      // the tab with unsynced taps still parked in a wait.
      for (const entry of queue) {
        if (entry.retryTimer !== null) {
          clearTimeout(entry.retryTimer)
          entry.retryTimer = null
        }
        entry.nextAttemptAt = 0
      }
      for (const entry of Array.from(coalesce.values())) {
        clearTimeout(entry.debounceTimer)
        flushCoalesce(entry.itemId)
      }
      runQueue()
    }),
  )

  function pendingCount(): number {
    return queue.length + coalesce.size
  }

  function dispatch(
    mutation: CaptureMutation,
  ): Promise<CaptureMutationResult> {
    if (mutation.kind === 'updateItem') {
      return dispatchUpdateItem(mutation)
    }
    return dispatchImmediate(mutation)
  }

  function dispatchImmediate(
    mutation: CaptureMutation,
  ): Promise<CaptureMutationResult> {
    return new Promise<CaptureMutationResult>((resolve, reject) => {
      const entry: QueueEntry = {
        id: nextEntryId++,
        mutation,
        resolvers: [{ resolve, reject }],
        attempts: 0,
        lwwRetried: false,
        inFlight: false,
        nextAttemptAt: 0,
        retryTimer: null,
        authBlocked: false,
        superseded: false,
      }
      queue.push(entry)
      runQueue()
    })
  }

  function dispatchUpdateItem(
    mutation: Extract<CaptureMutation, { kind: 'updateItem' }>,
  ): Promise<CaptureMutationResult> {
    return new Promise<CaptureMutationResult>((resolve, reject) => {
      const resolver: Resolvers = { resolve, reject }
      // Supersede any older `updateItem` for the same itemId already
      // past the coalesce window (queued or in-flight) — without
      // this, an in-flight remove's 409 LWW-retry can re-apply a
      // stale isZeroConfirmed=true on top of a re-add, hiding the
      // asset again. Same-itemId entries still inside the coalesce
      // window are handled by the latest-tap-wins merge below.
      // (Codex P2 on PR #416.)
      const stolenResolvers = supersedeQueuedUpdateItem(mutation.itemId)
      const existing = coalesce.get(mutation.itemId)
      if (existing !== undefined) {
        // Keep the LATEST payload — server cares about final state,
        // not the sum of deltas (§8 critical + PRD §7.3.4).
        clearTimeout(existing.debounceTimer)
        existing.current = mutation
        existing.resolvers.push(resolver, ...stolenResolvers)
        existing.debounceTimer = setTimeout(() => {
          flushCoalesce(mutation.itemId)
        }, debounceMs)
        return
      }
      const entry: CoalesceEntry = {
        itemId: mutation.itemId,
        current: mutation,
        resolvers: [resolver, ...stolenResolvers],
        debounceTimer: setTimeout(() => {
          flushCoalesce(mutation.itemId)
        }, debounceMs),
      }
      coalesce.set(mutation.itemId, entry)
    })
  }

  // Marks any queued/in-flight updateItem entry for this itemId as
  // superseded. In-flight entries are flagged so handleLwwConflict
  // skips the LWW retry; not-yet-running entries are pulled out of
  // the queue and their resolvers returned so the new dispatch can
  // adopt them (otherwise the original promises would hang).
  function supersedeQueuedUpdateItem(itemId: string): Resolvers[] {
    const stolen: Resolvers[] = []
    for (let i = queue.length - 1; i >= 0; i--) {
      const entry = queue[i]
      if (entry === undefined) continue
      if (entry.mutation.kind !== 'updateItem') continue
      if (entry.mutation.itemId !== itemId) continue
      entry.superseded = true
      if (!entry.inFlight) {
        stolen.push(...entry.resolvers)
        removeEntry(entry)
      }
    }
    return stolen
  }

  function flushCoalesce(itemId: string): void {
    const entry = coalesce.get(itemId)
    if (entry === undefined) return
    clearTimeout(entry.debounceTimer)
    coalesce.delete(itemId)
    // Canonicalise the committed quantity so a half-typed measurement draft
    // ("5." from the decimal tap, "" from Clear) never reaches the server —
    // contract parity with the PowerSync adapter + the connector send guard
    // (quantity-canonical.ts). Identity on an already-canonical value; forwards
    // an un-repairable value so the backend stays the authoritative validator.
    const committedQuantity =
      canonicalizeQuantity(String(entry.current.quantity)) ??
      entry.current.quantity
    queue.push({
      id: nextEntryId++,
      mutation: { ...entry.current, quantity: committedQuantity },
      resolvers: entry.resolvers,
      attempts: 0,
      lwwRetried: false,
      inFlight: false,
      nextAttemptAt: 0,
      retryTimer: null,
      authBlocked: false,
      superseded: false,
    })
    runQueue()
  }

  // Worker loop — scans the queue once per tick and kicks every
  // eligible entry. Concurrency across distinct entries is safe
  // because updateItem is coalesced per itemId — typically at most
  // one runnable entry per itemId is present (a superseded in-flight
  // entry can co-exist with its successor, but the superseded one
  // is already running and won't be re-picked here).
  function runQueue(): void {
    if (!isOnline()) return
    for (const entry of queue) {
      if (entry.inFlight) continue
      if (entry.authBlocked) continue
      if (entry.retryTimer !== null) continue
      if (entry.nextAttemptAt > 0 && entry.nextAttemptAt > Date.now()) continue
      void processEntry(entry)
    }
  }

  async function processEntry(entry: QueueEntry): Promise<void> {
    entry.inFlight = true
    entry.attempts += 1
    try {
      const result = await dispatchToServer(entry.mutation, fetchImpl)
      removeEntry(entry)
      // Suppress the item-changed event for a superseded entry's
      // success — local state is already tracking the newer
      // mutation's intent and emitting the stale row would briefly
      // flicker the asset back to its pre-supersede state. The
      // newer entry's own success will produce the authoritative
      // event. (Codex P2 on PR #416.)
      if (!entry.superseded) {
        bus.emit(eventForResult(entry.mutation, result))
      }
      for (const resolver of entry.resolvers) resolver.resolve(result)
    } catch (err) {
      entry.inFlight = false
      handleFailure(entry, err)
    }
  }

  function handleFailure(entry: QueueEntry, err: unknown): void {
    if (err instanceof ApiError) {
      emit({ name: 'mutation.failed', errorCode: err.code ?? String(err.status) })
      if (
        err.status === 409 &&
        err.code === 'failed_precondition' &&
        entry.mutation.kind === 'updateItem'
      ) {
        handleLwwConflict(entry, err)
        return
      }
      // 401 — auth expired mid-capture (20-ui §8.6a).
      // Mutation stays in queue; optimistic state is NOT rolled back.
      // One auth-expired event is emitted per episode (de-duplicated so
      // rapid-fire failures don't flood the UI). Queue drains when the
      // online listener fires after the user re-authenticates.
      if (err.status === 401) {
        entry.authBlocked = true
        if (!authExpiredEmitted) {
          bus.emit({ kind: 'auth-expired' })
          authExpiredEmitted = true
        }
        return
      }
      if (isPermanentClientError(err.status)) {
        removeEntry(entry)
        for (const resolver of entry.resolvers) resolver.reject(err)
        return
      }
      if (err.status === 429) {
        const retryAfterMs = extractRetryAfterMs(err.body)
        scheduleRetry(entry, retryAfterMs ?? computeBackoffMs(entry))
        return
      }
      if (err.status >= 500) {
        scheduleRetry(entry, computeBackoffMs(entry))
        return
      }
      // Unknown 4xx we didn't enumerate — treat as permanent so a
      // client bug doesn't spin the retry loop forever.
      removeEntry(entry)
      for (const resolver of entry.resolvers) resolver.reject(err)
      return
    }
    emit({ name: 'mutation.failed', errorCode: 'network_error' })
    scheduleRetry(entry, computeBackoffMs(entry))
  }

  function handleLwwConflict(entry: QueueEntry, err: ApiError): void {
    const authoritative = extractAuthoritative(err.body)
    if (authoritative !== null && entry.mutation.kind === 'updateItem') {
      bus.emit({
        kind: 'item-reconciled',
        itemId: entry.mutation.itemId,
        item: authoritative,
      })
      // Superseded entries must NOT LWW-retry: a newer dispatch has
      // taken over the user's intent for this itemId, and replaying
      // this stale payload with a refreshed syncVersion would clobber
      // the newer mutation (e.g. a confirmed remove re-applied on top
      // of a re-add, making the asset disappear). Drop quietly — the
      // item-reconciled event above lets consumers reconcile their
      // optimistic state if they were tracking this entry. (Codex P2
      // on PR #416.)
      if (entry.superseded) {
        removeEntry(entry)
        for (const resolver of entry.resolvers) {
          resolver.resolve({ kind: 'updateItem', item: authoritative })
        }
        return
      }
      if (!entry.lwwRetried) {
        const nextSyncVersion = extractSyncVersion(authoritative)
        if (nextSyncVersion !== null) {
          entry.mutation = {
            ...entry.mutation,
            syncVersion: nextSyncVersion,
          }
          entry.lwwRetried = true
          runQueue()
          return
        }
      }
    }
    removeEntry(entry)
    for (const resolver of entry.resolvers) resolver.reject(err)
  }

  function scheduleRetry(entry: QueueEntry, delayMs: number): void {
    entry.nextAttemptAt = Date.now() + delayMs
    entry.retryTimer = setTimeout(() => {
      entry.retryTimer = null
      runQueue()
    }, delayMs)
  }

  function computeBackoffMs(entry: QueueEntry): number {
    // `attempts` is already incremented, so the first failure
    // (attempts=1) yields `initialBackoffMs`.
    const exp = Math.max(0, entry.attempts - 1)
    const raw = initialBackoffMs * 2 ** exp
    return Math.min(raw, maxBackoffMs)
  }

  function removeEntry(entry: QueueEntry): void {
    const idx = queue.indexOf(entry)
    if (idx >= 0) queue.splice(idx, 1)
    if (entry.retryTimer !== null) {
      clearTimeout(entry.retryTimer)
      entry.retryTimer = null
    }
  }

  function subscribe(handler: CaptureEventHandler): () => void {
    return bus.subscribe(handler)
  }

  // Provided for CaptureClientWithInternals uniformity (the logout registry
  // flushes every client the same way). The REST adapter offers NO
  // logout-loss-prevention guarantee: its queue is in-memory only, so the
  // logout held-count guard (which reads PowerSync's durable ps_crud +
  // quarantine) sees nothing here regardless. We collapse the open coalesce
  // windows so a pending tap leaves the debounce and enters the queue — the
  // same best-effort drain the visibilitychange handler does — but on the
  // REST path a navigating sign-out can still abandon in-flight requests.
  // Real durability lives on the PowerSync adapter; production runs PowerSync.
  function flush(): Promise<void> {
    for (const entry of Array.from(coalesce.values())) {
      clearTimeout(entry.debounceTimer)
      flushCoalesce(entry.itemId)
    }
    return Promise.resolve()
  }

  function destroy(): void {
    for (const cleanup of cleanups) cleanup()
    cleanups.length = 0
    for (const entry of queue) {
      if (entry.retryTimer !== null) clearTimeout(entry.retryTimer)
    }
    queue.length = 0
    for (const entry of coalesce.values()) {
      clearTimeout(entry.debounceTimer)
    }
    coalesce.clear()
  }

  return { dispatch, subscribe, pendingCount, flush, destroy }
}
